import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

// Validation functions
const validateName = (name) => {
  if (!name || !name.trim()) {
    return { valid: false, error: "Name is required" };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length < 2) {
    return { valid: false, error: "Name must be at least 2 characters long" };
  }
  if (trimmedName.length > 100) {
    return { valid: false, error: "Name must not exceed 100 characters" };
  }
  
  // Check for numbers
  if (/\d/.test(trimmedName)) {
    return { valid: false, error: "Name cannot contain numbers" };
  }
  
  // Check for disallowed special characters
  const disallowed = trimmedName.match(/[_+@#$%^&*=[\]{}|\\<>/?~`!;:",]/g);
  if (disallowed) {
    return { valid: false, error: `Name contains invalid characters: ${[...new Set(disallowed)].join(', ')}` };
  }
  
  // Must contain at least some letters
  if (!/[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]/.test(trimmedName)) {
    return { valid: false, error: "Name must contain letters" };
  }
  
  // Cannot start or end with special characters
  if (/^[-'.]/.test(trimmedName)) {
    return { valid: false, error: "Name cannot start with a hyphen, apostrophe, or period" };
  }
  if (/[-']$/.test(trimmedName)) {
    return { valid: false, error: "Name cannot end with a hyphen or apostrophe" };
  }
  
  // No consecutive special characters
  if (/[-'.]{2,}/.test(trimmedName)) {
    return { valid: false, error: "Name cannot have consecutive special characters" };
  }
  
  return { valid: true, error: "" };
};

const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return { valid: false, error: "Email is required" };
  }
  
  const trimmedEmail = email.trim().toLowerCase();
  
  // Basic email format
  if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedEmail)) {
    return { valid: false, error: "Invalid email format" };
  }
  
  const localPart = trimmedEmail.split('@')[0];
  
  if (localPart.length < 2) {
    return { valid: false, error: "Email username must be at least 2 characters" };
  }
  
  // Block + signs
  if (localPart.includes('+')) {
    return { valid: false, error: "Email addresses with '+' are not allowed. Please use your primary email address." };
  }
  
  // Block excessive numbers
  if (/\d{5,}/.test(localPart)) {
    return { valid: false, error: "Please use a valid business email address." };
  }
  
  // Block timestamp patterns
  if (/20\d{6}/.test(localPart) || /\d{6}_\d{6}/.test(localPart)) {
    return { valid: false, error: "Please use a valid business email address." };
  }
  
  // Block test prefixes
  const testPrefixes = ['test_', 'test-', 'partner_', 'admin_', 'owner_', 'no_nda_', 'user_', 'demo_', 'temp_', 'qa_', 'dev_'];
  for (const prefix of testPrefixes) {
    if (localPart.startsWith(prefix)) {
      return { valid: false, error: "Please use your valid business email address." };
    }
  }
  
  // Block too many underscores
  if ((localPart.match(/_/g) || []).length > 2) {
    return { valid: false, error: "Please use a valid business email address." };
  }
  
  return { valid: true, error: "" };
};

function Register({ onLogin }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    country: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Validate on blur
    if (name === 'name' && value) {
      const result = validateName(value);
      if (!result.valid) {
        setFieldErrors({ ...fieldErrors, name: result.error });
      }
    }
    if (name === 'email' && value) {
      const result = validateEmail(value);
      if (!result.valid) {
        setFieldErrors({ ...fieldErrors, email: result.error });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const nameValidation = validateName(formData.name);
    const emailValidation = validateEmail(formData.email);
    
    if (!nameValidation.valid || !emailValidation.valid) {
      setFieldErrors({
        name: nameValidation.error,
        email: emailValidation.error
      });
      if (!nameValidation.valid) {
        toast.error(nameValidation.error);
      } else if (!emailValidation.valid) {
        toast.error(emailValidation.error);
      }
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      let data = {};
      
      // Read response as text first, then parse as JSON
      try {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        // If parsing fails, data remains empty object
      }

      if (response.ok) {
        toast.success('Registration successful!');
        onLogin(data.user, data.token);
      } else {
        // Show the actual error from the backend
        const errorMessage = data.detail || data.message || 
          (response.status === 400 ? 'Registration failed. Please check your information.' :
           'Registration failed. Please try again.');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // LevelUp Logo SVG component with accessibility
  const LevelUpLogo = () => (
    <svg 
      width="32" 
      height="32" 
      viewBox="0 0 32 32" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="img"
    >
      <rect x="2" y="20" width="6" height="10" rx="1" fill="#C4E538"/>
      <rect x="13" y="14" width="6" height="16" rx="1" fill="#C4E538"/>
      <rect x="24" y="6" width="6" height="24" rx="1" fill="#C4E538"/>
      <path d="M27 2L27 8M27 2L23 6M27 2L31 6" stroke="#C4E538" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Microsoft Logo component with accessibility
  const MicrosoftLogo = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 21 21" 
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="img"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = '#1B5E5F';
    e.target.style.boxShadow = '0 0 0 3px rgba(27,94,95,0.2)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = '#D1D5DB';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#1B5E5F',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Skip Link for Accessibility */}
      <a 
        href="#main-content" 
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.left = '16px';
          e.target.style.top = '16px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.padding = '8px 16px';
          e.target.style.backgroundColor = '#FFFFFF';
          e.target.style.color = '#1B5E5F';
          e.target.style.zIndex = '9999';
          e.target.style.borderRadius = '4px';
        }}
        onBlur={(e) => {
          e.target.style.left = '-9999px';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
        }}
      >
        Skip to main content
      </a>

      {/* Header Bar */}
      <header 
        role="banner"
        style={{ 
          backgroundColor: '#1B5E5F', 
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          padding: '16px 0'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <nav aria-label="Main branding" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <img src="/levelup-logo.png" alt="LevelUp" style={{ height: '32px', width: 'auto' }} />
            <span style={{ color: 'rgba(255,255,255,0.3)' }} aria-hidden="true">|</span>
            <img src="/microsoft-sponsored-logo.png" alt="Sponsored by Microsoft" style={{ height: '28px', width: 'auto' }} />
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main 
        id="main-content"
        role="main"
        style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '40px 24px'
        }}
      >
        <div style={{ width: '100%', maxWidth: '420px' }}>
          {/* Register Card */}
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            borderRadius: '12px', 
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }}>
            {/* Form Section */}
            <div style={{ padding: '40px 32px' }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <h1 style={{ fontSize: '28px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                  Create Account
                </h1>
                <p style={{ fontSize: '14px', color: '#4B5563' }}>
                  Join as a Microsoft Partner
                </p>
              </div>

              <form onSubmit={handleSubmit} data-testid="register-form" noValidate>
                <div style={{ marginBottom: '16px' }}>
                  <label 
                    htmlFor="name-input"
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}
                  >
                    Full Name <span aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <input
                    id="name-input"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={(e) => { handleInputBlur(e); handleBlur(e); }}
                    required
                    aria-required="true"
                    aria-invalid={!!fieldErrors.name}
                    aria-describedby={fieldErrors.name ? "name-error" : undefined}
                    autoComplete="name"
                    placeholder="Enter your full name"
                    style={{
                      ...inputStyle,
                      borderColor: fieldErrors.name ? '#DC2626' : inputStyle.borderColor
                    }}
                    onFocus={handleInputFocus}
                  />
                  {fieldErrors.name && (
                    <p id="name-error" style={{ color: '#DC2626', fontSize: '12px', marginTop: '4px', margin: '4px 0 0 0' }}>
                      {fieldErrors.name}
                    </p>
                  )}
                  <p style={{ color: '#6B7280', fontSize: '11px', marginTop: '4px', margin: '4px 0 0 0' }}>
                    Letters only (including hyphens and apostrophes for names like O'Brien or Mary-Jane)
                  </p>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label 
                    htmlFor="email-input"
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}
                  >
                    Email Address <span aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <input
                    id="email-input"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={(e) => { handleInputBlur(e); handleBlur(e); }}
                    required
                    aria-required="true"
                    aria-invalid={!!fieldErrors.email}
                    aria-describedby={fieldErrors.email ? "email-error" : undefined}
                    autoComplete="email"
                    placeholder="your.email@company.com"
                    style={{
                      ...inputStyle,
                      borderColor: fieldErrors.email ? '#DC2626' : inputStyle.borderColor
                    }}
                    onFocus={handleInputFocus}
                  />
                  {fieldErrors.email && (
                    <p id="email-error" style={{ color: '#DC2626', fontSize: '12px', marginTop: '4px', margin: '4px 0 0 0' }}>
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label 
                    htmlFor="password-input"
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}
                  >
                    Password <span aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="password-input"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      minLength={6}
                      autoComplete="new-password"
                      aria-describedby="password-hint"
                      placeholder="Min 6 characters"
                      style={{ ...inputStyle, paddingRight: '44px' }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6B7280',
                        padding: '4px'
                      }}
                    >
                      {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                    </button>
                  </div>
                  <p id="password-hint" style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                    Password must be at least 6 characters long
                  </p>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label 
                    htmlFor="country-input"
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}
                  >
                    Country <span aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <input
                    id="country-input"
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    autoComplete="country-name"
                    placeholder="e.g., United States"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  aria-disabled={loading}
                  data-testid="register-form-submit-button"
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: loading ? '#6B7280' : '#0C6A1D',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                    boxShadow: '0 2px 8px rgba(12,106,29,0.3)'
                  }}
                  onFocus={(e) => { e.target.style.outline = '2px solid #1B5E5F'; e.target.style.outlineOffset = '2px'; }}
                  onBlur={(e) => { e.target.style.outline = 'none'; }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>

              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <p style={{ fontSize: '14px', color: '#4B5563' }}>
                  Already have an account?{' '}
                  <Link 
                    to="/login" 
                    style={{ 
                      color: '#0C6A1D', 
                      fontWeight: '600', 
                      textDecoration: 'underline' 
                    }}
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Footer text - improved contrast */}
          <p style={{ 
            textAlign: 'center', 
            marginTop: '24px', 
            fontSize: '12px', 
            color: '#B2DFDB'
          }}>
            Secure partner portal for compete content
          </p>
        </div>
      </main>

      {/* Hidden styles for screen readers */}
      <style>{`
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </div>
  );
}

export default Register;
